import { normalizeHex, DEFAULT_ACCENT } from './nexastayTheme';
import { digitsOnly } from '../utils/phoneValidation';

const RAZORPAY_CHECKOUT_JS = 'https://checkout.razorpay.com/v1/checkout.js';

let razorpayScriptPromise = null;

export async function loadRazorpayScript() {
  if (typeof window === 'undefined') return false;
  if (window.Razorpay) return true;
  if (razorpayScriptPromise) return razorpayScriptPromise;

  razorpayScriptPromise = new Promise((resolve) => {
    const finalize = (loaded) => {
      resolve(Boolean(loaded && window.Razorpay));
    };
    const timeoutId = window.setTimeout(() => finalize(Boolean(window.Razorpay)), 12000);
    const script = document.createElement('script');
    script.src = RAZORPAY_CHECKOUT_JS;
    script.async = true;
    script.onload = () => {
      window.clearTimeout(timeoutId);
      finalize(true);
    };
    script.onerror = () => {
      window.clearTimeout(timeoutId);
      razorpayScriptPromise = null;
      finalize(false);
    };
    document.body.appendChild(script);
  });

  return razorpayScriptPromise;
}

/** Razorpay Standard Checkout branding aligned with NexaStay theme. */
export function buildRazorpayBranding({ theme, hotel } = {}) {
  const accent = normalizeHex(theme?.accent) || DEFAULT_ACCENT;
  const branding = {
    theme: {
      color: accent,
    },
  };

  const logoUrl = String(hotel?.logo_url || '').trim();
  if (/^https?:\/\//i.test(logoUrl)) {
    branding.image = logoUrl;
  }

  return branding;
}

function normalizeIndiaMobileDigits(digits) {
  let d = digitsOnly(digits);
  if (d.length === 12 && d.startsWith('91')) d = d.slice(2);
  if (d.length === 11 && d.startsWith('0')) d = d.slice(1);
  return d;
}

function isIndiaDialCode(countryCode) {
  const cc = String(countryCode || '').trim();
  return cc === '+91' || cc === '91';
}

/** Razorpay expects prefill.contact as +{country code}{national number}. */
export function buildRazorpayContactPrefill(phone, countryCode = '+91') {
  const rawDigits = digitsOnly(phone);
  if (!rawDigits) return '';

  const dialCode = String(countryCode || '+91').trim() || '+91';
  const dialDigits = dialCode.replace(/\D/g, '') || '91';
  const phoneDigits = isIndiaDialCode(dialCode)
    ? normalizeIndiaMobileDigits(rawDigits)
    : rawDigits;

  if (!phoneDigits) return '';
  return `+${dialDigits}${phoneDigits}`;
}

export function computeAmountDueToday(payableAmount, paymentMethod, partialPayPercent = 30) {
  const total = Math.max(0, Number(payableAmount) || 0);
  const method = String(paymentMethod || '').toLowerCase();
  if (method === 'pay_at_hotel') return 0;
  if (method === 'full_pay') return Math.round(total * 100) / 100;
  if (method === 'partial_pay') {
    const percent = Math.max(1, Math.min(99, Number(partialPayPercent) || 30));
    return Math.round((total * percent) / 100 * 100) / 100;
  }
  return 0;
}
